import { displayPrice, isPriceVariationsEnabled } from '../../util/configHelpers';
import { formatMoney } from '../../util/currency';
import { richText } from '../../util/richText';
import { isBookingProcessAlias } from '../../transactions/transaction';
import { types as sdkTypes } from '../../util/sdkLoader';

import css from './ListingCard.module.css';

const { Money } = sdkTypes;

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const FEE_PERCENT = parseFloat(process.env.REACT_APP_DEFAULT_CUSTOMER_FEE_PERCENT) || 8.5;
const MINIMUM_FEE = parseInt(process.env.REACT_APP_MINIMUM_CUSTOMER_FEE, 10) || 0;

// Returns a Money instance with the customer fee (percent-based, subject to minimum) rolled in.
const withAllInPrice = price => {
  if (price?.amount == null) return price;
  const calculatedFee = Math.round(price.amount * (FEE_PERCENT / 100));
  const actualFee = Math.max(calculatedFee, MINIMUM_FEE);
  return new Money(price.amount + actualFee, price.currency);
};

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTooltip: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTooltip: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

/**
 * Returns all translated and formatted strings for ListingCard so the
 * presentational component can stay simple and aria-labels use the same copy.
 *
 * @param {Object} listing - API entity: listing or ownListing
 * @param {Object} config - app configuration (e.g. from useConfiguration())
 * @param {Object} intl - React Intl instance (e.g. from useIntl())
 * @returns {Object} translations and derived values:
 *   - titlePlain: raw title string (for aria/alt)
 *   - titleFormatted: React nodes from richText(title) for display
 *   - showPrice: whether to show the price block
 *   - priceTooltip: string for the price element's title attribute (tooltip on hover)
 *   - priceMessage: string or null for the price block content (same translation as used in cardAriaLabel when shown)
 *   - cardAriaLabel: ready-to-use aria-label for the card link (listing title + price line when shown)
 *   - authorName: "ListingCard.author" string containing author's display name
 */
export const getListingCardTranslations = (listing, config, intl) => {
  const { title = '', price, publicData } = listing?.attributes || {};

  const authorDisplayName = listing?.author?.attributes?.profile?.displayName;
  const authorName = intl.formatMessage(
    { id: 'ListingCard.author' },
    { authorName: authorDisplayName }
  );

  const validListingTypes = config.listing.listingTypes || [];
  const { listingType } = publicData || {};
  const listingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);

  const showPrice = displayPrice(listingTypeConfig);

  const rentalSlots = publicData?.rental_slots;
  const hasPricingFromSlots = Array.isArray(rentalSlots) && rentalSlots.length > 0;
  const basePrice = (() => {
    if (hasPricingFromSlots && price) {
      const bestRateCents = Math.min(...rentalSlots.map(s => s.price / s.hours));
      return new Money(Math.ceil(bestRateCents / 100) * 100, price.currency);
    }
    return price;
  })();

  // Round the all-in price up to the nearest whole dollar so the formatter never shows cents.
  const allInRaw = withAllInPrice(basePrice);
  const allInPrice = allInRaw
    ? new Money(Math.ceil(allInRaw.amount / 100) * 100, allInRaw.currency)
    : allInRaw;

  const { formattedPrice, priceTooltip } = priceData(allInPrice, config.currency, intl);

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, listingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;
  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);

  const minimumHours = publicData?.minimum_hours || null;
  const isDockRampDelivery = listingType === 'dock-ramp-delivery';

  const priceMessageId = hasMultiplePriceVariants
    ? 'ListingCard.priceStartingFrom'
    : 'ListingCard.price';

  const perUnitString = isBookable && !isDockRampDelivery
    ? intl.formatMessage({ id: 'ListingCard.perUnit' }, { unitType: publicData?.unitType })
    : '';

  // Single formatted price line (amount + per-unit if applicable); used for both card aria and price block
  const priceValue = <span className={css.priceValue}>{formattedPrice}{(hasPricingFromSlots || isDockRampDelivery) ? '+' : ''}</span>;
  const pricePerUnit = isBookable && !isDockRampDelivery ? <span className={css.perUnit}>{perUnitString}</span> : '';
  const priceMessage =
    showPrice && formattedPrice != null
      ? intl.formatMessage({ id: priceMessageId }, { priceValue, pricePerUnit })
      : '';

  const cardAriaLabel =
    priceMessage.length > 0
      ? intl.formatMessage(
          { id: 'ListingCard.screenreader.label' },
          { listingTitle: title, formattedPrice: priceMessage }
        )
      : title;

  return {
    titlePlain: title,
    titleFormatted: richText(title, {
      longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
      longWordClass: css.longWord,
    }),
    authorName,
    showPrice,
    priceTooltip,
    priceMessage,
    cardAriaLabel,
    minimumHours,
    hasPricingFromSlots,
  };
};
